import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import * as React from 'react';
import styled from 'styled-components';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing2x, spacing4x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { Button } from '../Button/Button.js';
import { Entity } from '../Entity/Entity.js';
import { IconStatusUrgent } from '../Icons/IconStatusUrgent.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

var Body = styled.div.withConfig({
  displayName: "CommentRowBase__Body",
  componentId: "sc-k1iwd4-0"
})(["&&{margin-top:", ";}"], spacing2x);
var FlexWrapper = styled.div.withConfig({
  displayName: "CommentRowBase__FlexWrapper",
  componentId: "sc-k1iwd4-1"
})(["&&{display:flex;}&& > button{flex:none;align-self:flex-end;}"]);
var CommentRowContainer = styled.article.withConfig({
  displayName: "CommentRowBase__CommentRowContainer",
  componentId: "sc-k1iwd4-2"
})(["&&{width:100%;padding:", ";padding-top:", ";}&&:hover,&&:focus{outline:none;background:", ";}"], spacing4x, spacing2x, function (_ref) {
  var theme = _ref.theme;
  return theme.neutralColors.pearl;
});
var ButtonContainer = styled.div.withConfig({
  displayName: "CommentRowBase__ButtonContainer",
  componentId: "sc-k1iwd4-3"
})(["&&{display:flex;justify-content:space-between;margin-top:", ";}"], spacing2x);
var StyledButton = styled(Button).withConfig({
  displayName: "CommentRowBase__StyledButton",
  componentId: "sc-k1iwd4-4"
})(["&&{margin-inline-end:", ";}"], spacing2x);
var ErrorText = styled.div.withConfig({
  displayName: "CommentRowBase__ErrorText",
  componentId: "sc-k1iwd4-5"
})(["&&{color:", ";font-size:12px;line-height:16px;padding-inline-start:", ";padding-inline-end:", ";}"], function (_ref2) {
  var theme = _ref2.theme;
  return theme.alertColors.error;
}, spacing2x, spacing2x);
var ErrorIcon = styled(IconStatusUrgent).withConfig({
  displayName: "CommentRowBase__ErrorIcon",
  componentId: "sc-k1iwd4-6"
})(["&&{color:", ";}"], function (_ref3) {
  var theme = _ref3.theme;
  return theme.alertColors.error;
});
var EditorWrapper = styled.div.withConfig({
  displayName: "CommentRowBase__EditorWrapper",
  componentId: "sc-k1iwd4-7"
})(["&&{margin-top:", ";width:100%;height:136px;}"], spacing2x);
var EditedLabel = styled.div.withConfig({
  displayName: "CommentRowBase__EditedLabel",
  componentId: "sc-k1iwd4-8"
})(["&&{color:", ";font-size:12px;}"], function (_ref4) {
  var theme = _ref4.theme;
  return theme.neutralColors.smoke;
});
var CommentRowBase = /*#__PURE__*/React.forwardRef(function CommentRowBase2(props, ref) {
  var cancelEditBtnProps = props.cancelEditBtnProps,
    children = props.children,
    date = props.date,
    editMessage = props.editMessage,
    editor = props.editor,
    errorMessage = props.errorMessage,
    isEditing = props.isEditing,
    primaryActions = props.primaryActions,
    saveEditBtnProps = props.saveEditBtnProps,
    secondaryActions = props.secondaryActions,
    user = props.user;
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  function renderEditingMode() {
    if (isEditing && editor && cancelEditBtnProps && saveEditBtnProps) {
      return /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(EditorWrapper, {
          children: editor
        }), /*#__PURE__*/jsxs(ButtonContainer, {
          children: [/*#__PURE__*/jsx(FlexWrapper, {
            "aria-live": "assertive",
            children: errorMessage !== null && errorMessage !== void 0 && errorMessage.length ? /*#__PURE__*/jsxs(Fragment, {
              children: [/*#__PURE__*/jsx(ErrorIcon, {
                height: 14,
                width: 14
              }), /*#__PURE__*/jsx(ErrorText, {
                children: errorMessage
              })]
            }) : null
          }), /*#__PURE__*/jsxs(FlexWrapper, {
            role: "group",
            children: [/*#__PURE__*/jsx(StyledButton, _objectSpread(_objectSpread({}, cancelEditBtnProps), {}, {
              variant: "tertiary"
            })), /*#__PURE__*/jsx(Button, _objectSpread(_objectSpread({}, saveEditBtnProps), {}, {
              variant: "primary"
            }))]
          })]
        })]
      });
    } else {
      console.error("%cCommentRow warning!\n%c`editor`, `cancelEditBtnProps`, and `saveEditBtnProps` must be defined when `isEditing` is true", "font-weight: bold;", "");
      return null;
    }
  }
  return /*#__PURE__*/jsxs(CommentRowContainer, {
    ref: ref,
    tabIndex: 0,
    children: [/*#__PURE__*/jsx(Entity, {
      imgProps: _objectSpread(_objectSpread({}, user), {}, {
        size: "lg",
        hideToolTip: true
      }),
      subject: (user === null || user === void 0 ? void 0 : user.name) || t("CommentRow.deactivatedLabel"),
      primaryActions: primaryActions,
      secondaryActions: secondaryActions,
      timestampProps: {
        date: date,
        tooltipProps: {
          position: "bottom"
        }
      }
    }), isEditing ? renderEditingMode() : /*#__PURE__*/jsxs(Body, {
      children: [children, editMessage && /*#__PURE__*/jsx(EditedLabel, {
        className: "semi-bold",
        children: editMessage
      })]
    })]
  });
});
CommentRowBase.displayName = "CommentRowBase";

export { CommentRowBase };
